import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Heading,
  Button,
  VStack,
  HStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Select,
  Switch,
  useToast,
  Spinner,
  Alert,
  AlertIcon,
  Badge,
  Flex,
  Text,
  Divider,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  BsPlus,
  BsPencil,
  BsTrash,
  BsChevronUp,
  BsChevronDown,
  BsArrowUp,
  BsArrowDown,
} from "react-icons/bs";
import AdminPanel from "../components/AdminPanel";
import {
  getSidebarModules,
  updateSidebarModules,
  deleteSidebarModule,
  ICON_MAP,
} from "../services/sidebarService";

const SidebarManagement = () => {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedModule, setSelectedModule] = useState(null);
  const [editingItems, setEditingItems] = useState([]);
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  // Form state for module
  const [formData, setFormData] = useState({
    heading: "",
    order: 0,
    isActive: true,
    items: [],
  });

  // Load modules on mount
  useEffect(() => {
    loadModules();
  }, []);

  const loadModules = async () => {
    try {
      setLoading(true);
      const data = await getSidebarModules();
      setModules(data || []);
    } catch (error) {
      toast({
        title: "Error loading modules",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedModule(null);
    setFormData({
      heading: "",
      order: modules.length,
      isActive: true,
      items: [],
    });
    setEditingItems([]);
    onOpen();
  };

  const handleEdit = (module) => {
    setSelectedModule(module);
    setFormData({
      heading: module.heading,
      order: module.order,
      isActive: module.isActive,
      items: module.items,
    });
    setEditingItems(module.items || []);
    onOpen();
  };

  const handleDelete = async (moduleId) => {
    if (!window.confirm("Are you sure you want to delete this module?")) {
      return;
    }

    try {
      await deleteSidebarModule(moduleId);
      toast({
        title: "Module deleted",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      loadModules();
    } catch (error) {
      toast({
        title: "Error deleting module",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleSave = async () => {
    try {
      // Validation
      if (!formData.heading.trim()) {
        toast({
          title: "Heading is required",
          status: "warning",
          duration: 2000,
          isClosable: true,
        });
        return;
      }

      // Prepare the module data
      const moduleData = {
        ...formData,
        items: editingItems,
      };

      // If editing, replace the module in the array
      let updatedModules;
      if (selectedModule) {
        updatedModules = modules.map((m) =>
          m._id === selectedModule._id ? { ...moduleData, _id: m._id } : m,
        );
      } else {
        // Creating new module
        updatedModules = [...modules, moduleData];
      }

      // Send to backend
      await updateSidebarModules(updatedModules);

      toast({
        title: selectedModule ? "Module updated" : "Module created",
        status: "success",
        duration: 2000,
        isClosable: true,
      });

      onClose();
      loadModules();
    } catch (error) {
      toast({
        title: "Error saving module",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleAddItem = () => {
    setEditingItems([
      ...editingItems,
      {
        label: "",
        to: "",
        icon: "BsPeople",
        order: editingItems.length,
        isActive: true,
        roles: [],
      },
    ]);
  };

  const handleRemoveItem = (index) => {
    setEditingItems(editingItems.filter((_, i) => i !== index));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...editingItems];
    newItems[index] = { ...newItems[index], [field]: value };
    setEditingItems(newItems);
  };

  const handleMoveItem = (index, direction) => {
    if (
      (direction === "up" && index === 0) ||
      (direction === "down" && index === editingItems.length - 1)
    ) {
      return;
    }

    const newItems = [...editingItems];
    const swapIndex = direction === "up" ? index - 1 : index + 1;
    [newItems[index], newItems[swapIndex]] = [
      newItems[swapIndex],
      newItems[index],
    ];

    // Update order values
    newItems.forEach((item, i) => {
      item.order = i;
    });

    setEditingItems(newItems);
  };

  const handleMoveModule = async (index, direction) => {
    if (
      (direction === "up" && index === 0) ||
      (direction === "down" && index === modules.length - 1)
    ) {
      return;
    }

    const newModules = [...modules];
    const swapIndex = direction === "up" ? index - 1 : index + 1;
    [newModules[index], newModules[swapIndex]] = [
      newModules[swapIndex],
      newModules[index],
    ];

    // Update order values
    newModules.forEach((module, i) => {
      module.order = i;
    });

    try {
      await updateSidebarModules(newModules);
      setModules(newModules);
      toast({
        title: "Order updated",
        status: "success",
        duration: 1000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Error updating order",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleToggleActive = async (module) => {
    try {
      const updatedModules = modules.map((m) =>
        m._id === module._id ? { ...m, isActive: !m.isActive } : m,
      );
      await updateSidebarModules(updatedModules);
      setModules(updatedModules);
      toast({
        title: "Status updated",
        status: "success",
        duration: 1000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Error updating status",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  if (loading) {
    return (
      <AdminPanel>
        <Flex justify="center" align="center" h="400px">
          <Spinner size="xl" color="teal.500" />
        </Flex>
      </AdminPanel>
    );
  }

  return (
    <AdminPanel>
      <Container maxW="container.xl" py={8}>
        <VStack spacing={6} align="stretch">
          {/* Header */}
          <Flex justify="space-between" align="center">
            <Box>
              <Heading size="lg">Sidebar Module Management</Heading>
              <Text color="gray.600" mt={1}>
                Manage navigation modules and menu items
              </Text>
            </Box>
            <Button
              leftIcon={<BsPlus />}
              colorScheme="teal"
              onClick={handleCreate}
            >
              Add Module
            </Button>
          </Flex>

          {/* Modules List */}
          {modules.length === 0 ? (
            <Alert status="info">
              <AlertIcon />
              No modules found. Click "Add Module" to create your first module.
            </Alert>
          ) : (
            <Accordion allowToggle>
              {modules.map((module, index) => (
                <AccordionItem
                  key={module._id}
                  border="1px"
                  borderColor={borderColor}
                  borderRadius="md"
                  mb={3}
                  bg={bgColor}
                >
                  <AccordionButton>
                    <Flex flex="1" align="center" gap={3}>
                      <Badge colorScheme={module.isActive ? "green" : "gray"}>
                        {module.isActive ? "Active" : "Inactive"}
                      </Badge>
                      <Text fontWeight="bold">{module.heading}</Text>
                      <Badge colorScheme="blue">
                        {module.items?.length || 0} items
                      </Badge>
                    </Flex>
                    <HStack spacing={2} mr={2}>
                      <IconButton
                        icon={<BsArrowUp />}
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMoveModule(index, "up");
                        }}
                        isDisabled={index === 0}
                        aria-label="Move up"
                      />
                      <IconButton
                        icon={<BsArrowDown />}
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMoveModule(index, "down");
                        }}
                        isDisabled={index === modules.length - 1}
                        aria-label="Move down"
                      />
                      <Switch
                        isChecked={module.isActive}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleToggleActive(module);
                        }}
                        colorScheme="teal"
                      />
                      <IconButton
                        icon={<BsPencil />}
                        size="sm"
                        colorScheme="blue"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(module);
                        }}
                        aria-label="Edit"
                      />
                      <IconButton
                        icon={<BsTrash />}
                        size="sm"
                        colorScheme="red"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(module._id);
                        }}
                        aria-label="Delete"
                      />
                    </HStack>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel pb={4}>
                    <Table size="sm" variant="simple">
                      <Thead>
                        <Tr>
                          <Th>Label</Th>
                          <Th>Path</Th>
                          <Th>Icon</Th>
                          <Th>Status</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {module.items?.map((item, idx) => (
                          <Tr key={idx}>
                            <Td>{item.label}</Td>
                            <Td>
                              <Text fontSize="sm" color="gray.600">
                                {item.to}
                              </Text>
                            </Td>
                            <Td>{item.icon}</Td>
                            <Td>
                              <Badge
                                colorScheme={item.isActive ? "green" : "gray"}
                              >
                                {item.isActive ? "Active" : "Inactive"}
                              </Badge>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </AccordionPanel>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </VStack>
      </Container>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="4xl"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {selectedModule ? "Edit Module" : "Create Module"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={6} align="stretch">
              {/* Module Details */}
              <Box p={4} borderWidth="1px" borderRadius="md">
                <Heading size="sm" mb={4}>
                  Module Details
                </Heading>
                <VStack spacing={4}>
                  <FormControl isRequired>
                    <FormLabel>Heading</FormLabel>
                    <Input
                      value={formData.heading}
                      onChange={(e) =>
                        setFormData({ ...formData, heading: e.target.value })
                      }
                      placeholder="e.g., Core HR"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Order</FormLabel>
                    <NumberInput
                      value={formData.order}
                      min={0}
                      onChange={(_, value) =>
                        setFormData({ ...formData, order: value })
                      }
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>

                  <FormControl display="flex" alignItems="center">
                    <FormLabel mb="0">Active</FormLabel>
                    <Switch
                      isChecked={formData.isActive}
                      onChange={(e) =>
                        setFormData({ ...formData, isActive: e.target.checked })
                      }
                      colorScheme="teal"
                    />
                  </FormControl>
                </VStack>
              </Box>

              {/* Menu Items */}
              <Box>
                <Flex justify="space-between" align="center" mb={4}>
                  <Heading size="sm">Menu Items</Heading>
                  <Button
                    size="sm"
                    leftIcon={<BsPlus />}
                    colorScheme="teal"
                    variant="outline"
                    onClick={handleAddItem}
                  >
                    Add Item
                  </Button>
                </Flex>

                <VStack spacing={4} align="stretch">
                  {editingItems.map((item, index) => (
                    <Box
                      key={index}
                      p={4}
                      borderWidth="1px"
                      borderRadius="md"
                      bg={useColorModeValue("gray.50", "gray.700")}
                    >
                      <Flex justify="space-between" mb={3}>
                        <Text fontWeight="bold">Item #{index + 1}</Text>
                        <HStack>
                          <IconButton
                            icon={<BsChevronUp />}
                            size="xs"
                            onClick={() => handleMoveItem(index, "up")}
                            isDisabled={index === 0}
                            aria-label="Move up"
                          />
                          <IconButton
                            icon={<BsChevronDown />}
                            size="xs"
                            onClick={() => handleMoveItem(index, "down")}
                            isDisabled={index === editingItems.length - 1}
                            aria-label="Move down"
                          />
                          <IconButton
                            icon={<BsTrash />}
                            size="xs"
                            colorScheme="red"
                            onClick={() => handleRemoveItem(index)}
                            aria-label="Remove"
                          />
                        </HStack>
                      </Flex>

                      <VStack spacing={3}>
                        <FormControl isRequired>
                          <FormLabel fontSize="sm">Label</FormLabel>
                          <Input
                            size="sm"
                            value={item.label}
                            onChange={(e) =>
                              handleItemChange(index, "label", e.target.value)
                            }
                            placeholder="e.g., Employee Database"
                          />
                        </FormControl>

                        <FormControl isRequired>
                          <FormLabel fontSize="sm">Path</FormLabel>
                          <Input
                            size="sm"
                            value={item.to}
                            onChange={(e) =>
                              handleItemChange(index, "to", e.target.value)
                            }
                            placeholder="e.g., /core-hr/employee-database"
                          />
                        </FormControl>

                        <FormControl>
                          <FormLabel fontSize="sm">Icon</FormLabel>
                          <Select
                            size="sm"
                            value={item.icon}
                            onChange={(e) =>
                              handleItemChange(index, "icon", e.target.value)
                            }
                          >
                            {Object.keys(ICON_MAP).map((iconName) => (
                              <option key={iconName} value={iconName}>
                                {iconName}
                              </option>
                            ))}
                          </Select>
                        </FormControl>

                        <FormControl display="flex" alignItems="center">
                          <FormLabel fontSize="sm" mb="0">
                            Active
                          </FormLabel>
                          <Switch
                            size="sm"
                            isChecked={item.isActive}
                            onChange={(e) =>
                              handleItemChange(
                                index,
                                "isActive",
                                e.target.checked,
                              )
                            }
                            colorScheme="teal"
                          />
                        </FormControl>
                      </VStack>
                    </Box>
                  ))}

                  {editingItems.length === 0 && (
                    <Text color="gray.500" textAlign="center" py={4}>
                      No items added yet. Click "Add Item" to create menu items.
                    </Text>
                  )}
                </VStack>
              </Box>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button colorScheme="teal" onClick={handleSave}>
              {selectedModule ? "Update" : "Create"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </AdminPanel>
  );
};

export default SidebarManagement;
